/**
 * useTaskCompletionReasons - Motivos de conclusão (Retenção / Jurídico)
 *
 * Cada módulo (retention | legal) possui sua própria lista de motivos, usada
 * obrigatoriamente ao concluir uma solicitação/processo. Todo o acesso é feito
 * por RPCs SECURITY DEFINER (nenhum SELECT direto em tabela).
 *
 * @version 1.2.0
 * RELEASE: 2026-08-22 v1.2.0 - is_retained substituído por outcome
 *          (retained | cancelled | neutral)
 * RELEASE: 2026-08-22 v1.0.0 - Criação inicial
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';
import type { TaskModuleType } from '@/hooks/useTaskModuleType';

const logger = createLogger('useTaskCompletionReasons');

/** Resultado possível do motivo de conclusão */
export type TaskCompletionOutcome = 'retained' | 'cancelled' | 'neutral';

/** Motivo de conclusão retornado pela RPC */
export interface TaskCompletionReason {
  id: string;
  task_type: TaskModuleType;
  name: string;
  description: string | null;
  color: string;
  sort_order: number;
  is_active: boolean;
  /** v1.2.0 — resultado do motivo: retido, cancelamento efetivado ou neutro */
  outcome: TaskCompletionOutcome;
  usage_count: number;
  created_at: string;
  updated_at: string;
}

/** Payload de criação/edição */
export interface UpsertTaskCompletionReasonInput {
  id?: string;
  name: string;
  description?: string | null;
  color?: string;
  sort_order?: number;
  is_active?: boolean;
  /** v1.2.0 — resultado do motivo */
  outcome?: TaskCompletionOutcome;
}

/**
 * Hook principal.
 *
 * @param taskType    Módulo dos motivos (retention | legal)
 * @param onlyActive  Quando true, retorna apenas motivos ativos (uso no modal de conclusão)
 * @param enabled     Permite adiar a query (ex.: modal fechado)
 */
export function useTaskCompletionReasons(
  taskType: TaskModuleType,
  onlyActive = false,
  enabled = true,
) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  // Motivos só existem para Retenção e Jurídico
  const isSupported = taskType === 'retention' || taskType === 'legal';

  const reasonsQuery = useQuery({
    queryKey: ['task-completion-reasons', currentTenant?.id, taskType, onlyActive],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_task_completion_reasons', {
        p_task_type: taskType as any,
        p_include_inactive: !onlyActive,
        p_tenant_hint: currentTenant?.id || null,
      } as any);

      if (error) {
        logger.error('Erro ao buscar motivos de conclusão', { error: error.message });
        throw error;
      }
      return (data || []) as TaskCompletionReason[];
    },
    enabled: !!currentTenant?.id && isSupported && enabled,
  });

  const upsertMutation = useMutation({
    mutationFn: async (input: UpsertTaskCompletionReasonInput) => {
      const { data, error } = await supabase.rpc('upsert_task_completion_reason', {
        p_task_type: taskType as any,
        p_name: input.name,
        p_id: input.id ?? null,
        p_description: input.description ?? null,
        p_color: input.color ?? '#64748b',
        p_sort_order: input.sort_order ?? 0,
        p_is_active: input.is_active ?? true,
        p_outcome: input.outcome ?? 'cancelled',
        p_tenant_hint: currentTenant?.id || null,
      } as any);

      if (error) {
        logger.error('Erro ao salvar motivo de conclusão', { error: error.message });
        throw error;
      }
      return data as string;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['task-completion-reasons'] });
      toast.success(variables.id ? 'Motivo atualizado' : 'Motivo criado');
    },
    onError: (error: Error) => toast.error(`Erro ao salvar motivo: ${error.message}`),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc('delete_task_completion_reason', {
        p_id: id,
        p_tenant_hint: currentTenant?.id || null,
      } as any);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-completion-reasons'] });
      toast.success('Motivo excluído');
    },
    onError: (error: Error) => toast.error(`Erro ao excluir motivo: ${error.message}`),
  });

  return {
    reasons: reasonsQuery.data || [],
    isLoading: reasonsQuery.isLoading,
    upsertReason: upsertMutation.mutateAsync,
    isSaving: upsertMutation.isPending,
    deleteReason: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
}
